import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'

import useApiService from 'services/api'
import LoadingScreen from 'components/Layout/LoadingScreen'
import {
  CompanyPageLayout,
  CompanyContentSections,
} from 'components/NewCompanyPage'
import { UnmatchedDoorsModal } from 'components/NewCompanyPage/Modals/UnmatchedDoorsModal'
import { InitialDoorData } from 'components/Samples/SampleRequestFormModal'
import {
  MetricCard,
  StatsCard,
  Divider,
  DescriptionAndContactInfo,
} from 'components/NewCompanyPage/Sidebar/shared'
import { CompanyTypeSlug } from 'models/companies'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { ContactCompanyType } from 'models/contact_companies'
import { formatAddress } from 'utils/formatting'
import useDocumentTitle from 'components/useDocumentTitle'

export function NewUnmatchedLocationPage({
  doorProxyID,
}: {
  doorProxyID: number
}) {
  const api = useApiService()
  const { data: doorProxyData, isLoading } = api.useGetDoorProxy(doorProxyID)
  const { preferences } = usePreferences()

  const chainProxyId = doorProxyData?.chain_proxy as number | undefined
  const { data: companyData } = api.useGetCompany(chainProxyId ?? 0)

  // Set document title
  useDocumentTitle(`${doorProxyData?.name || 'Not Found'} (Door) - First Bite`)

  // Modal state management
  const [showDoorsModal, setShowDoorsModal] = React.useState(false)

  // Pre-compute door data for sample requests to avoid redundant API calls
  // The data is already loaded via useGetDoorProxy
  // For unmatched doors, use door proxy data if available, fall back to company data
  const initialDoorData: InitialDoorData | undefined = useMemo(() => {
    if (!doorProxyData || !companyData) return undefined

    return {
      name: doorProxyData.name || companyData.name || '',
      address: doorProxyData.address || companyData.address || '',
      city: doorProxyData.city || companyData.city || '',
      state: doorProxyData.state || companyData.state || '',
      zipcode: doorProxyData.zipcode || companyData.zipcode || '',
      companyName: companyData.name || '',
    }
  }, [doorProxyData, companyData])

  const entityBreadcrumbs = useMemo(
    () => [
      ...(companyData?.name
        ? [
            {
              title: 'Company',
              label: companyData.name,
              path: `/company/${preferences?.company_type_prefix_map[ContactCompanyType.UserGenerated]}${chainProxyId}`,
            },
          ]
        : []),
      ...(doorProxyData?.name
        ? [{ title: 'Door', label: doorProxyData.name }]
        : []),
    ],
    [
      companyData?.name,
      preferences?.company_type_prefix_map,
      chainProxyId,
      doorProxyData?.name,
    ]
  )

  const categoryInfo = useMemo(
    () =>
      companyData?.category_slug && companyData?.sub_category?.slug
        ? {
            categorySlug: companyData.category_slug,
            subCategorySlug: companyData.sub_category.slug,
          }
        : undefined,
    [companyData?.category_slug, companyData?.sub_category?.slug]
  )

  if (isLoading || !companyData) {
    return <LoadingScreen />
  }

  // Build stats for the summary tab
  const companyStats: Array<{
    label: string
    value: React.ReactNode
    url?: string
  }> = [
    {
      label: 'Category',
      value: companyData?.category || '-',
    },
    {
      label: 'Sub-Category',
      value: (
        <Link
          className="text-fb-green-900 decoration-fb-green-700 underline"
          to={`/targets/${companyData?.category_slug}/${companyData?.sub_category?.slug}/`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {companyData?.sub_category?.name || '-'}
        </Link>
      ),
    },
  ]

  // Build summary tab content
  const summaryTabContent: React.ReactNode[] = []

  const hasAddress =
    companyData?.address &&
    companyData?.city &&
    companyData?.state &&
    companyData?.zipcode

  if (hasAddress) {
    summaryTabContent.push(
      <Divider key="divider-1" />,
      <DescriptionAndContactInfo
        key="description-contact"
        address={
          formatAddress({
            street: companyData?.address,
            city: companyData?.city,
            state: companyData?.state,
            zip: companyData?.zipcode,
          }) ?? undefined
        }
        phone={companyData?.phone_number || undefined}
        googlePlaceUrl={companyData.google_place_url}
      />
    )
  }

  summaryTabContent.push(<StatsCard key="stats" stats={companyStats} />)

  // Build location details tab content
  const locationDetailsTabContent: React.ReactNode[] = []

  // Company Location
  const companyLocation = companyData?.address
    ? `${companyData.city}, ${companyData.state} ${companyData.zipcode}`
    : 'N/A'

  locationDetailsTabContent.push(
    <MetricCard
      key="company-location"
      value={companyLocation}
      label="Company Location"
      variant="full-width"
    />
  )

  // Google Place URL if available
  if (companyData?.google_place_url) {
    locationDetailsTabContent.push(<Divider key="divider-location" />)
    locationDetailsTabContent.push(
      <div key="google-place" className="space-y-2">
        <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
          Google Place
        </h3>
        <a
          href={companyData.google_place_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-green-700 hover:text-green-800 underline break-all"
        >
          View on Google Maps
        </a>
      </div>
    )
  }

  // Additional location info
  const locationInfoStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Phone',
      value: companyData?.phone_number
        ? `${companyData.phone_number}${
            companyData.extension ? ` ext. ${companyData.extension}` : ''
          }`
        : 'N/A',
    },
    {
      label: 'Full Address',
      value: companyData?.address || 'N/A',
    },
    {
      label: 'City',
      value: companyData?.city || 'N/A',
    },
    {
      label: 'State',
      value: companyData?.state || 'N/A',
    },
    {
      label: 'ZIP Code',
      value: companyData?.zipcode || 'N/A',
    },
  ]

  locationDetailsTabContent.push(<Divider key="divider-2" />)
  locationDetailsTabContent.push(
    <StatsCard key="location-info" stats={locationInfoStats} />
  )

  return (
    <>
      <CompanyPageLayout
        chainProxyId={companyData?.id as number}
        breadcrumbRoot={`${companyData?.category || 'Commercial'} - ${
          companyData?.sub_category?.name || 'Company'
        }`}
        breadcrumbs={[
          {
            title: 'Company',
            breadcrumb: companyData?.name || '',
            url: `/company/${preferences?.company_type_prefix_map[ContactCompanyType.UserGenerated]}${chainProxyId}`,
          },
          {
            title: 'Door',
            breadcrumb: doorProxyData?.name || '',
            url: undefined,
          },
        ]}
        entityBreadcrumbs={entityBreadcrumbs}
        categoryInfo={categoryInfo}
        sidebar={{
          header: {
            name: companyData?.name || '',
            domain: companyData?.domain || '',
            isUserCreated: true,
          },
          tabs: [
            {
              key: 'summary',
              title: 'Summary',
              content: summaryTabContent,
            },
            {
              key: 'location-details',
              title: 'Location Details',
              content: locationDetailsTabContent,
            },
          ],
        }}
      >
        <CompanyContentSections
          chainProxyId={companyData?.id as number}
          doorProxyId={doorProxyID}
          companyName={companyData?.name || ''}
          websiteAddress={companyData?.domain || ''}
          companyTypeSlug={companyData?.sub_category?.slug as CompanyTypeSlug}
          tableTypeSlug="unmatched-doors-table"
          isUserCreated={true}
          isDoorPage={true}
          initialDoorData={initialDoorData}
        />
      </CompanyPageLayout>

      {/* Unmatched Doors Modal */}
      {companyData && (
        <UnmatchedDoorsModal
          show={showDoorsModal}
          onHide={() => setShowDoorsModal(false)}
          forChainProxyId={companyData?.id as number}
          chainData={{
            id: companyData?.id as number,
            name: companyData?.name || '',
            domain: companyData?.domain || '',
          }}
          subCategory={companyData?.sub_category}
          companyName={companyData?.name || ''}
        />
      )}
    </>
  )
}
